/**
 * Design Shape map + resolver (Phase 56.1 / BLU-536).
 *
 * A print filename is `{designId}{variantSuffix}-{shapeCode}-{colorCode}.arx4`;
 * the middle shapeCode (`R6`, `H4`, ...) = f(product type, Design Shape, size).
 * This module resolves that function: the operator-facing Design Shape names
 * (Rectangular / Square / Horizontal / Circle) cross the product-type code and
 * size code to yield the file code.
 *
 * DEFAULT_DESIGN_SHAPE_MAP is the TH catalog seed, re-keyed from
 * `.planning/todos/Product builder mapping.json` (the authoritative source,
 * verified against prod GCS) at IMPLEMENTATION time — the JSON is never read
 * at runtime. Re-keying applied:
 *   - productTypes inverted long-name → SKU code (`T-Shirt`→`TEE`,
 *     `Oversized Tee`→`HT`, ...).
 *   - sizes inverted to SKU size codes (`2XL`→`2X`, `3XL`→`3X`, `4XL`→`4X`;
 *     all others identity, kids numeric sizes included).
 *   - `Tote Bag` has NO size dimension in the source (`{shape: code}`), so it
 *     is flattened to the NO_SIZE_KEY sentinel for uniform lookups.
 *
 * SECURITY: this resolver is the layer that prevents an operator-set metafield
 * value (or a corrupted org-supplied JSONB map) from reaching a storage
 * filename. The downstream FILENAME_REGEX permits `.` and `-`, so traversal /
 * separator injection must die here:
 *   - designShape is checked against a STRICT 4-name allowlist.
 *   - the looked-up code is checked against `^[A-Za-z0-9]+$` at RUNTIME on
 *     EVERY call (org maps flow through the same resolver — a leaf like
 *     `R6-CO` or `x.y` would survive FILENAME_REGEX and corrupt
 *     parseDerivedFilename's right-to-left segmentation).
 * `null` means "caller falls back to the SKU's parsed printId" — the resolver
 * never throws.
 */
/** The four operator-facing Design Shape names. STRICT allowlist — anything else resolves null. */
export const DESIGN_SHAPES = ['Rectangular', 'Square', 'Horizontal', 'Circle'];
/**
 * Sentinel size key for product types with no size dimension (Tote Bag).
 * Seed-time flattening writes the single code under this key; the resolver
 * falls back to it when the passed size code has no entry.
 */
export const NO_SIZE_KEY = '__nosize__';
/** Runtime output-safety check applied to every emitted code. */
const SHAPE_CODE_REGEX = /^[A-Za-z0-9]+$/;
/**
 * TH catalog Design Shape map, re-keyed from the product builder mapping JSON.
 *
 * Notes from re-keying (preserve verbatim — this is reference data):
 * - `Kids Tee` and `Kids T-Shirt` both map to the `KTEE` code with IDENTICAL
 *   tables in the source JSON (verified cell-by-cell), so they collapse to the
 *   single KTEE entry below.
 * - Hoodie (HD) and Sweatshirt (SWT) share the T-Shirt table in the source;
 *   kept as explicit duplicates — the map is reference data, not code.
 * - Oversized Tee (HT) has a 4X column ONLY for Rectangular in the source;
 *   Square/Horizontal/Circle stop at 3X. Women's Tee (WOM) stops at 2X.
 *   Baby Tee (BYT) / Longline Baby Tee (LLBYT) run XS–XL only. Preserved
 *   as-is: an absent size resolves null (caller falls back to SKU printId).
 * - Boxy Crop Tee (BXCP) and Cropped Oversized Tee (COS) have identical
 *   tables in the source; kept as explicit duplicates.
 * - Dual product-type codes (`2TEE`, `2HD`, ...) are NOT seeded — the
 *   resolver strips the leading `2` on lookup.
 */
export const DEFAULT_DESIGN_SHAPE_MAP = {
    // T-Shirt
    TEE: {
        Rectangular: { XS: 'R4', S: 'R4', M: 'R5', L: 'R5', XL: 'R6', '2X': 'R6', '3X': 'R7', '4X': 'R7' },
        Square: { XS: 'S3', S: 'S3', M: 'S4', L: 'S4', XL: 'S4', '2X': 'S5', '3X': 'S5', '4X': 'S5' },
        Horizontal: { XS: 'H3', S: 'H3', M: 'H4', L: 'H4', XL: 'H4', '2X': 'H5', '3X': 'H5', '4X': 'H5' },
        Circle: { XS: 'C3', S: 'C3', M: 'C4', L: 'C4', XL: 'C5', '2X': 'C5', '3X': 'C6', '4X': 'C6' },
    },
    // Oversized Tee — front Horizontal steps on a DIFFERENT size scale than back
    // Rectangular (XL+ jumps R7→R9 but H5→H6); this asymmetry is the MSB bug crux.
    HT: {
        Rectangular: { XS: 'R7', S: 'R7', M: 'R7', L: 'R7', XL: 'R9', '2X': 'R9', '3X': 'R9', '4X': 'R9' },
        Square: { XS: 'S5', S: 'S5', M: 'S5', L: 'S5', XL: 'S6', '2X': 'S6', '3X': 'S6' },
        Horizontal: { XS: 'H5', S: 'H5', M: 'H5', L: 'H5', XL: 'H6', '2X': 'H6', '3X': 'H6' },
        Circle: { XS: 'C6', S: 'C6', M: 'C6', L: 'C6', XL: 'C7', '2X': 'C7', '3X': 'C7' },
    },
    // Hoodie — identical to TEE in the source JSON (explicit duplicate, reference data)
    HD: {
        Rectangular: { XS: 'R4', S: 'R4', M: 'R5', L: 'R5', XL: 'R6', '2X': 'R6', '3X': 'R7', '4X': 'R7' },
        Square: { XS: 'S3', S: 'S3', M: 'S4', L: 'S4', XL: 'S4', '2X': 'S5', '3X': 'S5', '4X': 'S5' },
        Horizontal: { XS: 'H3', S: 'H3', M: 'H4', L: 'H4', XL: 'H4', '2X': 'H5', '3X': 'H5', '4X': 'H5' },
        Circle: { XS: 'C3', S: 'C3', M: 'C4', L: 'C4', XL: 'C5', '2X': 'C5', '3X': 'C6', '4X': 'C6' },
    },
    // Sweatshirt — identical to TEE in the source JSON (explicit duplicate, reference data)
    SWT: {
        Rectangular: { XS: 'R4', S: 'R4', M: 'R5', L: 'R5', XL: 'R6', '2X': 'R6', '3X': 'R7', '4X': 'R7' },
        Square: { XS: 'S3', S: 'S3', M: 'S4', L: 'S4', XL: 'S4', '2X': 'S5', '3X': 'S5', '4X': 'S5' },
        Horizontal: { XS: 'H3', S: 'H3', M: 'H4', L: 'H4', XL: 'H4', '2X': 'H5', '3X': 'H5', '4X': 'H5' },
        Circle: { XS: 'C3', S: 'C3', M: 'C4', L: 'C4', XL: 'C5', '2X': 'C5', '3X': 'C6', '4X': 'C6' },
    },
    // Baby Tee — single code per shape across XS–XL
    BYT: {
        Rectangular: { XS: 'R0', S: 'R0', M: 'R0', L: 'R0', XL: 'R0' },
        Square: { XS: 'S1', S: 'S1', M: 'S1', L: 'S1', XL: 'S1' },
        Horizontal: { XS: 'H1', S: 'H1', M: 'H1', L: 'H1', XL: 'H1' },
        Circle: { XS: 'C1', S: 'C1', M: 'C1', L: 'C1', XL: 'C1' },
    },
    // Kids Tee + Kids T-Shirt — BOTH long names map to KTEE in the source JSON
    // with identical tables (verified), so they collapse to this single entry.
    KTEE: {
        Rectangular: { '4': 'R1', '6': 'R1', '8': 'R2', '10': 'R2', '12': 'R2', '14': 'R3', '16': 'R3' },
        Square: { '4': 'S1', '6': 'S1', '8': 'S1', '10': 'S2', '12': 'S2', '14': 'S2', '16': 'S2' },
        Horizontal: { '4': 'H1', '6': 'H1', '8': 'H1', '10': 'H2', '12': 'H2', '14': 'H2', '16': 'H2' },
        Circle: { '4': 'C1', '6': 'C1', '8': 'C1', '10': 'C2', '12': 'C2', '14': 'C2', '16': 'C2' },
    },
    // Tote Bag — no size dimension in the source ({shape: code}); flattened to
    // the NO_SIZE_KEY sentinel so lookups stay uniform.
    TB: {
        Rectangular: { [NO_SIZE_KEY]: 'R3' },
        Square: { [NO_SIZE_KEY]: 'S2' },
        Horizontal: { [NO_SIZE_KEY]: 'H2' },
        Circle: { [NO_SIZE_KEY]: 'C2' },
    },
    // Longline Baby Tee — identical to BYT in the source JSON (explicit duplicate)
    LLBYT: {
        Rectangular: { XS: 'R0', S: 'R0', M: 'R0', L: 'R0', XL: 'R0' },
        Square: { XS: 'S1', S: 'S1', M: 'S1', L: 'S1', XL: 'S1' },
        Horizontal: { XS: 'H1', S: 'H1', M: 'H1', L: 'H1', XL: 'H1' },
        Circle: { XS: 'C1', S: 'C1', M: 'C1', L: 'C1', XL: 'C1' },
    },
    // Boxy Crop Tee — Horizontal is non-monotonic at the small end (XS H4, S H5)
    BXCP: {
        Rectangular: { XS: 'R5', S: 'R5', M: 'R5', L: 'R6', XL: 'R6', '2X': 'R6', '3X': 'R7' },
        Square: { XS: 'S4', S: 'S4', M: 'S4', L: 'S5', XL: 'S5', '2X': 'S5', '3X': 'S5' },
        Horizontal: { XS: 'H4', S: 'H5', M: 'H5', L: 'H5', XL: 'H5', '2X': 'H5', '3X': 'H5' },
        Circle: { XS: 'C3', S: 'C3', M: 'C4', L: 'C4', XL: 'C4', '2X': 'C5', '3X': 'C5' },
    },
    // Women's Tee — runs XS–2X only in the source
    WOM: {
        Rectangular: { XS: 'R3', S: 'R4', M: 'R4', L: 'R4', XL: 'R4', '2X': 'R5' },
        Square: { XS: 'S2', S: 'S3', M: 'S3', L: 'S3', XL: 'S4', '2X': 'S4' },
        Horizontal: { XS: 'H3', S: 'H4', M: 'H4', L: 'H4', XL: 'H4', '2X': 'H5' },
        Circle: { XS: 'C2', S: 'C3', M: 'C3', L: 'C3', XL: 'C3', '2X': 'C3' },
    },
    // Cropped Oversized Tee — identical to BXCP in the source JSON (explicit duplicate)
    COS: {
        Rectangular: { XS: 'R5', S: 'R5', M: 'R5', L: 'R6', XL: 'R6', '2X': 'R6', '3X': 'R7' },
        Square: { XS: 'S4', S: 'S4', M: 'S4', L: 'S5', XL: 'S5', '2X': 'S5', '3X': 'S5' },
        Horizontal: { XS: 'H4', S: 'H5', M: 'H5', L: 'H5', XL: 'H5', '2X': 'H5', '3X': 'H5' },
        Circle: { XS: 'C3', S: 'C3', M: 'C4', L: 'C4', XL: 'C4', '2X': 'C5', '3X': 'C5' },
    },
};
/**
 * Own-property read that tolerates malformed (org-supplied JSONB) values:
 * non-objects, arrays, null, and inherited/prototype keys all yield undefined.
 */
function getOwn(obj, key) {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return undefined;
    }
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        return undefined;
    }
    return obj[key];
}
/**
 * Resolve a Design Shape to its print-file shape code.
 *
 * Lookup: `map[productTypeCode][designShape][sizeCode]`, with:
 * - dual `2` prefix stripped from the product-type code when the raw code has
 *   no entry (`2HD` → `HD`);
 * - fallback to the NO_SIZE_KEY sentinel for unsized types (Tote Bag);
 * - STRICT allowlist on designShape (only the 4 operator-facing names);
 * - a RUNTIME `^[A-Za-z0-9]+$` check on the looked-up value, on EVERY call —
 *   org-supplied maps flow through here and the result becomes part of a
 *   storage filename.
 *
 * Returns the shape code, or `null` for any unknown/invalid input — the caller
 * falls back to the SKU's parsed printId. Never throws.
 */
export function resolveDesignShapeCode(map, productTypeCode, designShape, sizeCode) {
    if (typeof productTypeCode !== 'string' ||
        typeof designShape !== 'string' ||
        typeof sizeCode !== 'string') {
        return null;
    }
    // Strict allowlist — anything outside the 4 names (wrong case, '../x', '')
    // is rejected before any map access.
    if (!DESIGN_SHAPES.includes(designShape)) {
        return null;
    }
    // Product-type lookup, with dual `2` prefix strip (2HD → HD). Unknown types
    // (e.g. 2TSW — a skuRules prefix with no shape mapping) resolve null; the
    // caller's SKU fallback covers them.
    let typeTable = getOwn(map, productTypeCode);
    if (typeTable === undefined && productTypeCode.startsWith('2')) {
        typeTable = getOwn(map, productTypeCode.slice(1));
    }
    const sizeTable = getOwn(typeTable, designShape);
    // Size lookup, falling back to the no-size sentinel (Tote Bag).
    let code = getOwn(sizeTable, sizeCode);
    if (code === undefined) {
        code = getOwn(sizeTable, NO_SIZE_KEY);
    }
    // RUNTIME output-safety gate: the result feeds a storage filename and the
    // downstream FILENAME_REGEX permits `.`/`-` — reject anything non-alphanumeric.
    if (typeof code !== 'string' || !SHAPE_CODE_REGEX.test(code)) {
        return null;
    }
    return code;
}
